import classNames from "classnames";
import * as React from "react";

import type {
  KnowledgeGraphSeed,
  KnowledgeGraphTrace,
  KnowledgeGraphTraceOutcome,
  KnowledgeGraphTracePath,
  KnowledgeGraphTraceStage,
  KnowledgeGraphTraceStageStatus,
  TracedCandidate,
} from "@/shared/domain/admin/ChatDebugRecord";
import { formatBoolean } from "@/shared/text";

import styles from "./KnowledgeGraphSection.module.css";
import { MemoryCard } from "./MemoryCard";
import { SEED_SOURCE_LABEL, SEED_SOURCE_ORDER } from "./seedSources";
import { formatRuntime } from "./tokenFormat";

type BadgeTone = "good" | "warn" | "bad" | "neutral";

/** Small pill used for path/outcome/stage-status labels. Tone drives color;
 *  `bad` marks what this view exists to make obvious: a retrieval that kept
 *  memory from the model, a failed stage, or a seed source this UI doesn't
 *  recognize. */
function Badge({ label, tone }: { label: string; tone: BadgeTone }) {
  return (
    <span className={styles.badge} data-tone={tone}>
      {label}
    </span>
  );
}

// Only `injected` puts new text in front of the model. An outcome naming a
// closed gate gets the same "bad" tone as a failed stage: a suppressed
// retrieval should look wrong, not merely different. The two
// `all_matches_already_*` outcomes are neutral because the model already
// holds every match, in the preferences block or on an earlier user message.
const OUTCOME_TONE: Record<KnowledgeGraphTraceOutcome, BadgeTone> = {
  injected: "good",
  no_seed_terms: "bad",
  no_candidates: "bad",
  all_matches_already_in_preferences: "neutral",
  all_matches_already_injected: "neutral",
  empty_render: "bad",
  failed: "bad",
};

const STAGE_STATUS_TONE: Record<KnowledgeGraphTraceStageStatus, BadgeTone> = {
  ok: "good",
  empty: "neutral",
  fallback: "warn",
  skipped: "neutral",
  failed: "bad",
};

// User-facing names for the two read paths — "turn_context"/"tool_search" are
// internal identifiers, not something an admin unfamiliar with the pipeline
// would recognize at a glance.
const PATH_LABEL: Record<KnowledgeGraphTracePath, string> = {
  turn_context: "per-turn injection",
  tool_search: "explicit tool query",
};

// Roughly the length of text three lines holds at the seed-text column's
// width — texts at or under this render in full, with no toggle to hide
// anything. Above it (typically the assistant message), the text starts
// clamped and needs a "Show more" click to read past the third line.
const SEED_TEXT_COLLAPSE_THRESHOLD = 180;

// Above this many prepared terms, the chip list starts collapsed — a pool
// this size is already more than fits on screen, so scanning it unfolded
// defeats the point of a debug view. Smaller pools stay open since there's
// nothing to hide.
const PREPARED_TERMS_COLLAPSE_THRESHOLD = 50;

function formatScore(value: number | null): string {
  return value === null ? "—" : value.toFixed(3);
}

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "boolean") {
    return formatBoolean(value);
  }
  if (typeof value === "number") {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * A stage's `detail` dict as key/value pairs. This is where the literal
 * FalkorDB query string lives — the single most valuable field in the whole
 * view — so values render monospace and wrap instead of clipping.
 */
function StageDetail({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail);
  if (entries.length === 0) {
    return null;
  }
  return (
    <dl className={styles.detailList}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.detailRow}>
          <dt className={styles.detailKey}>{key}</dt>
          <dd className={styles.detailValue}>{formatDetailValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A stage's candidate pool, already ranked — rendered in the given order,
 * not re-sorted. `rendered` rows (the ones that survived into the model's
 * prompt) get a highlighted background so the gap between a high-ranked
 * candidate and one the budget or a dedupe dropped is visible at a glance.
 */
function CandidatesTable({
  candidates,
  candidateCount,
}: {
  candidates: TracedCandidate[];
  candidateCount: number;
}) {
  if (candidates.length === 0) {
    return null;
  }
  // Only the rerank stage populates rerank_relevance — omit the column
  // entirely on every other stage rather than show a dash for every row.
  const hasRerank = candidates.some(
    (candidate) => candidate.rerank_relevance !== null,
  );
  return (
    <div className={styles.candidatesWrapper}>
      <table className={styles.candidatesTable}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Kind</th>
            <th className={styles.numeric}>Score</th>
            {hasRerank && <th className={styles.numeric}>Rerank</th>}
            <th className={styles.numeric}>Importance</th>
            <th className={styles.numeric}>Usage</th>
            <th>Convergent</th>
            <th>Contradicted</th>
            <th>Edge</th>
            <th>Rendered</th>
            <th>Preview</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate, index) => (
            <tr
              key={`${candidate.name}-${index}`}
              data-rendered={candidate.rendered ? "true" : undefined}
            >
              <td>{candidate.name}</td>
              <td>{candidate.kind ?? "—"}</td>
              <td className={styles.numeric}>{formatScore(candidate.score)}</td>
              {hasRerank && (
                <td className={styles.numeric}>
                  {formatScore(candidate.rerank_relevance)}
                </td>
              )}
              <td className={styles.numeric}>
                {formatScore(candidate.importance)}
              </td>
              <td className={styles.numeric}>
                {formatScore(candidate.usage_score)}
              </td>
              <td>{candidate.convergent ? "✓" : ""}</td>
              <td>{candidate.contradicted ? "✓" : ""}</td>
              <td className={styles.edge}>{candidate.edge ?? "—"}</td>
              <td>{candidate.rendered ? "✓" : ""}</td>
              <td className={styles.preview}>
                {candidate.content_preview ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {candidates.length < candidateCount && (
        <div className={styles.capNote}>
          showing {candidates.length} of {candidateCount}
        </div>
      )}
    </div>
  );
}

/** One collapsible stage row: name, status, timing, and its detail/candidates
 *  once expanded. Collapsed by default — the trace-level summary already
 *  surfaces the outcome that matters most. */
function StageRow({ stage }: { stage: KnowledgeGraphTraceStage }) {
  return (
    <details className={styles.stage}>
      <summary className={styles.stageSummary}>
        <span className={styles.stageName}>{stage.name}</span>
        <Badge label={stage.status} tone={STAGE_STATUS_TONE[stage.status]} />
        <span className={styles.stageMeta}>
          {formatRuntime(stage.duration_ms)} · {stage.candidate_count} candidate
          {stage.candidate_count === 1 ? "" : "s"}
        </span>
      </summary>
      <StageDetail detail={stage.detail} />
      <CandidatesTable
        candidates={stage.candidates}
        candidateCount={stage.candidate_count}
      />
    </details>
  );
}

/**
 * Legacy fallback for traces recorded before `seeds` shipped: a flat,
 * priority-ordered list of raw terms with no source or usage metadata.
 * Element 0 is labeled primary — it's the latest user message, and what the
 * reranker ranks against — the same thing the `latest_user_message` group
 * means in the richer `seeds` view, just without the shape to say so
 * directly.
 */
function LegacySeedTerms({ seedTerms }: { seedTerms: string[] }) {
  if (seedTerms.length === 0) {
    return null;
  }
  return (
    <div className={styles.termRow}>
      <span className={styles.termLabel}>Seed terms:</span>
      {seedTerms.map((term, index) => (
        <span key={index} className={styles.term}>
          {term}
          {index === 0 && <span className={styles.primaryTag}> (primary)</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * One seed's text, collapsed to ~3 lines with a "Show more" toggle once it's
 * long enough to need one — the latest assistant message is routinely long
 * enough to swamp the shorter seeds around it otherwise. Short seeds (most
 * of the other sources) render in full with no toggle at all.
 */
function SeedText({ text }: { text: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const isLong = text.length > SEED_TEXT_COLLAPSE_THRESHOLD;
  return (
    <div className={styles.seedTextWrapper}>
      <p
        className={classNames(styles.seedText, {
          [styles.seedTextClamped]: isLong && !expanded,
        })}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => {
            setExpanded((previous) => !previous);
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

/** Groups seeds by source, preserving each group's original relative order. */
function groupSeedsBySource(
  seeds: KnowledgeGraphSeed[],
): Map<string, KnowledgeGraphSeed[]> {
  const groups = new Map<string, KnowledgeGraphSeed[]>();
  for (const seed of seeds) {
    const group = groups.get(seed.source);
    if (group) {
      group.push(seed);
    } else {
      groups.set(seed.source, [seed]);
    }
  }
  return groups;
}

interface SeedGroup {
  source: string;
  label: string;
  /** Whether this client knows the source, i.e. it is in `SEED_SOURCE_ORDER`. */
  recognized: boolean;
  seeds: KnowledgeGraphSeed[];
}

/**
 * Seed groups in render order: the sources this client knows, in
 * `SEED_SOURCE_ORDER`, then anything left over.
 *
 * The leftover pass is the point. Rendering only known sources meant a source
 * added server-side vanished here — no group, no count mismatch, no warning —
 * from the one view whose job is explaining what retrieval saw. An unknown
 * source now renders under its raw name, flagged, so the gap is visible
 * instead of invisible.
 */
function orderSeedGroups(seeds: KnowledgeGraphSeed[]): SeedGroup[] {
  const grouped = groupSeedsBySource(seeds);
  const groups: SeedGroup[] = [];
  for (const source of SEED_SOURCE_ORDER) {
    const group = grouped.get(source);
    if (group) {
      groups.push({
        source,
        label: SEED_SOURCE_LABEL[source],
        recognized: true,
        seeds: group,
      });
      grouped.delete(source);
    }
  }
  for (const [source, group] of grouped) {
    groups.push({
      source,
      label: `Unrecognized source: ${source}`,
      recognized: false,
      seeds: group,
    });
  }
  return groups;
}

/**
 * Seeds grouped under a human-labeled heading per source (see
 * `orderSeedGroups`). Each seed shows a badge naming how it was used —
 * `embedded + full-text` when it was also vectorized, `full-text only`
 * otherwise — so which seeds fed vector search is visible without
 * cross-referencing the stage detail.
 */
function SeedsBySource({ seeds }: { seeds: KnowledgeGraphSeed[] }) {
  return (
    <div className={styles.seedsSection}>
      {orderSeedGroups(seeds).map((group) => (
        <div key={group.source} className={styles.seedGroup}>
          <div className={styles.seedGroupLabel}>
            {group.label}
            {!group.recognized && (
              <Badge label="unknown to this UI" tone="bad" />
            )}
          </div>
          {group.seeds.map((seed, index) => (
            <div key={index} className={styles.seedRow}>
              <Badge
                label={
                  seed.vector_seed ? "embedded + full-text" : "full-text only"
                }
                tone={seed.vector_seed ? "good" : "neutral"}
              />
              <SeedText text={seed.text} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The trace's seeds, in whichever shape it was recorded in: the grouped,
 * source-attributed view when `seeds` is present, or the flat legacy list
 * built from `seed_terms` for traces recorded before `seeds` shipped.
 */
function SeedsSection({ trace }: { trace: KnowledgeGraphTrace }) {
  if (trace.seeds && trace.seeds.length > 0) {
    return <SeedsBySource seeds={trace.seeds} />;
  }
  return <LegacySeedTerms seedTerms={trace.seed_terms} />;
}

/**
 * Post-tokenization terms actually searched, as wrapped monospace chips
 * behind a count. An empty pool alongside non-empty seeds is called out
 * explicitly — it's the `no_seed_terms` gate, the first thing to suspect
 * when nothing surfaced. A pool over
 * `PREPARED_TERMS_COLLAPSE_THRESHOLD` starts collapsed, since scanning 50+
 * chips unfolded defeats the point of a debug view.
 */
function PreparedTerms({ preparedTerms }: { preparedTerms: string[] }) {
  if (preparedTerms.length === 0) {
    return (
      <div className={styles.termRow}>
        <span className={styles.termLabel}>Prepared terms:</span>
        <span className={styles.gateNote}>
          none — seeds tokenized to nothing (no_seed_terms gate)
        </span>
      </div>
    );
  }
  return (
    <details
      className={styles.preparedTerms}
      open={preparedTerms.length <= PREPARED_TERMS_COLLAPSE_THRESHOLD}
    >
      <summary className={styles.preparedTermsSummary}>
        Prepared terms ({preparedTerms.length})
      </summary>
      <div className={styles.termChips}>
        {preparedTerms.map((term, index) => (
          <span key={index} className={styles.termChip}>
            {term}
          </span>
        ))}
      </div>
    </details>
  );
}

function TraceTextBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className={styles.blockWrapper}>
      <div className={styles.blockLabel}>{label}</div>
      <pre className={styles.block}>{text}</pre>
    </div>
  );
}

// The stage the backend records for the turn-stable preferences block. Its
// `rendered` flags mean "made the preferences block", not "made the injected
// block", so the two card builders below must not read each other's stage.
const PREFERENCES_STAGE_NAME = "preferences";

/**
 * The final rendered pool: the candidates that survived into the block the
 * model saw, taken from the last stage that flagged any (the render stage's
 * view — final ordering, post-budget, post-dedupe). Earlier stages' `rendered`
 * flags mark the same survivors mid-pipeline; the last stage is authoritative.
 * The preferences stage is skipped outright — its rendered candidates went
 * into the system prompt's preferences block, not the injected turn block.
 */
function finalRenderedCandidates(
  trace: KnowledgeGraphTrace,
): TracedCandidate[] {
  for (let index = trace.stages.length - 1; index >= 0; index--) {
    const stage = trace.stages[index];
    if (stage.name === PREFERENCES_STAGE_NAME) {
      continue;
    }
    const rendered = stage.candidates.filter((candidate) => candidate.rendered);
    if (rendered.length > 0) {
      return rendered;
    }
  }
  return [];
}

/**
 * The candidates that made the preferences block, from the trace's
 * preferences stage — the stage records exactly the selected set, with
 * `rendered` set for the names that reached the block. Empty on traces
 * recorded before the stage carried candidates, which is the signal to
 * degrade to the raw block text.
 */
function preferencesStageCandidates(
  trace: KnowledgeGraphTrace,
): TracedCandidate[] {
  for (let index = trace.stages.length - 1; index >= 0; index--) {
    const stage = trace.stages[index];
    if (stage.name !== PREFERENCES_STAGE_NAME) {
      continue;
    }
    const rendered = stage.candidates.filter((candidate) => candidate.rendered);
    if (rendered.length > 0) {
      return rendered;
    }
  }
  return [];
}

/**
 * Mirror of the backend's attribution resolution (`memory_attribution`):
 * `"you"` iff the trace recorded a caller principal and the candidate's
 * author matches it. A candidate with no recorded author gets no chip at all
 * rather than a guessed one.
 */
function candidateAttribution(
  candidate: TracedCandidate,
  trace: KnowledgeGraphTrace,
): string | null {
  if (candidate.source_principal === null) {
    return null;
  }
  return trace.caller_principal !== null &&
    candidate.source_principal === trace.caller_principal
    ? "you"
    : "a teammate";
}

/**
 * A rendered memory block for admin legibility: one `MemoryCard` per
 * candidate that reached the model, built from the trace's structured fields
 * (never by parsing the block text) — kind badge, [you]/[a teammate] chip,
 * content preview as the primary line, evidence behind a disclosure, edge
 * provenance when present. The verbatim block stays one click away as the
 * authoritative record of the exact bytes sent. Serves both blocks a trace
 * carries: the per-turn injected block and the system prompt's preferences
 * block, each fed by its own stage's rendered candidates.
 */
function TraceMemoryCards({
  trace,
  memories,
  label,
  verbatimLabel,
  verbatimText,
}: {
  trace: KnowledgeGraphTrace;
  memories: TracedCandidate[];
  label: string;
  verbatimLabel: string;
  verbatimText: string;
}) {
  return (
    <div className={styles.blockWrapper}>
      <div className={styles.blockLabel}>{label}</div>
      <div className={styles.memoryCards}>
        {memories.map((candidate, index) => (
          <MemoryCard
            key={`${candidate.name}-${index}`}
            name={candidate.name}
            kind={candidate.kind}
            attribution={candidateAttribution(candidate, trace)}
            content={candidate.content_preview}
            evidence={candidate.evidence}
            edge={candidate.edge}
          />
        ))}
      </div>
      <details className={styles.verbatimBlock}>
        <summary className={styles.verbatimBlockSummary}>
          {verbatimLabel}
        </summary>
        <pre className={styles.block}>{verbatimText}</pre>
      </details>
    </div>
  );
}

/**
 * One retrieval trace: header badges (path, outcome, timing, reranker),
 * seed/prepared terms, every stage the pipeline ran, any CONTRADICTS
 * warnings, and the text the model actually saw. Expanded by default when
 * the outcome is a failure (`bad` tone): that's the "why did memory not
 * surface" case, and it shouldn't take an extra click to see why.
 */
function TraceRow({ trace }: { trace: KnowledgeGraphTrace }) {
  // Structure only from captured fields: when no stage flagged rendered
  // candidates (older traces, or shapes this view doesn't recognize), the
  // injected block degrades to its raw text rendering.
  const injectedMemories =
    trace.injected_block !== null ? finalRenderedCandidates(trace) : [];
  let injectedContent: React.ReactNode;
  if (trace.injected_block === null || trace.injected_block === "") {
    injectedContent = (
      <div className={styles.gateNote}>
        {OUTCOME_TONE[trace.outcome] === "neutral"
          ? "Every match is already in front of the model (in the preferences, or on an earlier user message)."
          : "Nothing was injected into the prompt for this retrieval."}
      </div>
    );
  } else if (injectedMemories.length > 0) {
    injectedContent = (
      <TraceMemoryCards
        trace={trace}
        memories={injectedMemories}
        label="Injected memories (what the model saw)"
        verbatimLabel="verbatim injected block"
        verbatimText={trace.injected_block}
      />
    );
  } else {
    injectedContent = (
      <TraceTextBlock
        label="Injected block (what the model saw)"
        text={trace.injected_block}
      />
    );
  }

  // Same degrade rule for the preferences block: cards when its stage
  // recorded the selected candidates, the raw block text when it didn't
  // (traces predating candidate capture on the preferences stage).
  const preferenceMemories = trace.preferences_block
    ? preferencesStageCandidates(trace)
    : [];
  return (
    <details
      className={styles.trace}
      open={OUTCOME_TONE[trace.outcome] === "bad"}
    >
      <summary className={styles.traceSummary}>
        <Badge label={PATH_LABEL[trace.path]} tone="neutral" />
        <Badge label={trace.outcome} tone={OUTCOME_TONE[trace.outcome]} />
        <span className={styles.traceMeta}>
          {formatRuntime(trace.duration_ms)} ·{" "}
          {`reranker ${trace.reranker_used ? "used" : "not used"}`}
        </span>
      </summary>
      <div className={styles.traceBody}>
        <SeedsSection trace={trace} />
        <PreparedTerms preparedTerms={trace.prepared_terms} />
        {trace.stages.map((stage, index) => (
          <StageRow key={`${stage.name}-${index}`} stage={stage} />
        ))}
        {trace.warnings.length > 0 && (
          <div className={styles.warnings}>
            {trace.warnings.map((warning, index) => (
              <div key={index} className={styles.warning}>
                {warning}
              </div>
            ))}
          </div>
        )}
        {trace.preferences_block &&
          (preferenceMemories.length > 0 ? (
            <TraceMemoryCards
              trace={trace}
              memories={preferenceMemories}
              label="Preferences (cached system prompt)"
              verbatimLabel="verbatim preferences block"
              verbatimText={trace.preferences_block}
            />
          ) : (
            <TraceTextBlock
              label="Preferences block"
              text={trace.preferences_block}
            />
          ))}
        {injectedContent}
      </div>
    </details>
  );
}

export interface KnowledgeGraphSectionProps {
  /** Knowledge-graph retrieval traces recorded for this turn, in the order
   *  they ran — the passive turn-context injection plus any
   *  `query_knowledge` tool calls. Empty on turns recorded before
   *  capture shipped. */
  traces: KnowledgeGraphTrace[];
}

/**
 * Collapsible "Knowledge Graph" section: renders inside
 * `MessageKnowledgeGraphButton`'s dialog, opened from a user message in the
 * admin thread explorer. One sub-section per retrieval trace shows the seed
 * terms, every stage the pipeline ran (with the literal query text each one
 * sent and the candidates it produced), any CONTRADICTS warnings, and the
 * text the model actually saw — enough to answer, stage by stage, why the
 * right memory did or didn't surface.
 */
export function KnowledgeGraphSection({ traces }: KnowledgeGraphSectionProps) {
  return (
    <details className={styles.section}>
      <summary className={styles.sectionSummary}>
        Knowledge Graph ({traces.length})
      </summary>
      <div className={styles.sectionBody}>
        {traces.length === 0 ? (
          <div className={styles.gateNote}>
            No knowledge-graph retrieval — neither the per-turn injection nor a
            query_knowledge tool call — was recorded for any of this turn&apos;s
            Bedrock calls. Either the turn predates capture, or none ran.
          </div>
        ) : (
          traces.map((trace, index) => <TraceRow key={index} trace={trace} />)
        )}
      </div>
    </details>
  );
}
